import type { DataGap } from '../types/siteEvidence';
import type { SiteRagReport } from '../types/siteRag';
import { effectiveGapImpact, gapShouldSurface, type EffectiveGapImpact } from './gapImpact';
import { filterStaleBlindSpotGaps } from './filterStaleBlindSpotGaps';

export interface ResolvedReportBlindSpot {
  gap: DataGap;
  impact: EffectiveGapImpact;
}

function legacyGap(text: string, kind: 'gap' | 'anomaly'): DataGap {
  return {
    field: kind === 'gap' ? 'Legacy evidence gap' : 'Evidence conflict',
    reason: text.trim(),
    impact: 'minor',
    recommendation: kind === 'gap'
      ? 'Confirm the missing evidence with the responsible provider or a qualified advisor.'
      : 'Reconcile the conflicting evidence against an authoritative source before relying on it.',
  };
}

/**
 * Resolve the immutable, customer-visible blind-spot rows from a report.
 *
 * Typed generation-time `dataGaps` are authoritative. Legacy reports fall
 * back to their string `sedGaps`; separately persisted evidence anomalies are
 * retained as conflicts. Verified provider references remove stale typed gaps,
 * and intent-aware severity removes rows that are not applicable to this site.
 * The document-model builder and every renderer use this same projection so a
 * report cannot promise a blind-spot block that its delivery artifact omits.
 */
export function resolveReportBlindSpots(report: SiteRagReport): ResolvedReportBlindSpot[] {
  const typed = filterStaleBlindSpotGaps(report.dataGaps ?? [], report.references);
  const base = report.dataGaps !== undefined
    ? typed
    : (report.sedGaps ?? []).filter((text) => text.trim()).map((text) => legacyGap(text, 'gap'));
  const anomalies = (report.sedAnomalies ?? [])
    .filter((text) => text.trim())
    .map((text) => legacyGap(text, 'anomaly'));

  return [...base, ...anomalies]
    .map((gap) => ({ gap, impact: effectiveGapImpact(gap, report.projectIntentAtGen) }))
    .filter((row) => gapShouldSurface(row.impact))
    .sort((left, right) => {
      const order: Record<EffectiveGapImpact, number> = {
        critical: 0,
        significant: 1,
        noted: 2,
        irrelevant: 3,
      };
      return order[left.impact] - order[right.impact];
    });
}
